import React, { useState } from 'react';

import { NewsFormCreate } from 'App/components/News/components/NewsForm';

import {
  SidePage,
  Action,
  Actions,
  SimpleDatePicker,
  Stack,
  Typography,
  AddCircleOutlineOutlinedIcon,
  FilterListIcon,
} from '@gogame/components';
import { useChildComponent } from '@gogame/hooks-ts';
import { DateValueFormatter } from '@gogame/intl-ts';
import { useIsAllowed } from '@gogame/permissions';

import { LEFT_DRAWER_Z_INDEX } from '../Sidebar/constants';
import NewsList from './NewsList';
import {
  NEWS_MANAGEMENT_PERMISSION,
  TYPE_DEFAULT,
  defaultType,
} from './constants';
import {
  useLoadRelease,
  useUpdateReleaseDate,
  useLoadFilterTypes,
  useLoadNews,
} from './data';

export const News = ({ open, onClose, onExited }) => {
  const [type, setType] = useState(TYPE_DEFAULT);
  const { data: releaseDate } = useLoadRelease();
  const { data: types } = useLoadFilterTypes();
  const { isLoading, isRefetching } = useLoadNews(type);
  const [updateRelease] = useUpdateReleaseDate();

  const [childComponent, openChildComponent] = useChildComponent();

  const handleCreate = () => {
    openChildComponent(NewsFormCreate);
  };

  const handleChangeReleaseDate = async (_e, date) => {
    updateRelease({ date });
  };

  const isAllowed = useIsAllowed();

  const commonTypes = types?.common || [];
  const technicalTypes = types?.technical || [];
  const reducedTypes = [defaultType, ...commonTypes, ...technicalTypes];

  return (
    <SidePage
      open={open}
      onClose={onClose}
      onExited={onExited}
      loading={isLoading}
      fetching={isRefetching}
      size={'m'}
      backdrop={false}
      direction={'right'}
      directionOfset={'64px'}
      zIndex={LEFT_DRAWER_Z_INDEX}
    >
      <SidePage.Title
        onClose={onClose}
        title={'News'}
        titleVariant={'h6'}
        subTitle={
          <Stack spacing={1} direction={'row'}>
            <Typography
              uppercase={true}
              variant={'overline'}
              color={'textSecondary'}
            >
              Next release:
            </Typography>
            <Typography variant={'overline'} color={'textSecondary'}>
              <DateValueFormatter
                value={releaseDate}
                template={'LL'}
                timeZone={'local'}
              />
            </Typography>
          </Stack>
        }
        actions={
          <Stack direction={'row'} spacing={0}>
            <Actions limit={3}>
              <Action
                badge={type !== TYPE_DEFAULT}
                icon={<FilterListIcon />}
                tooltip={'Filter'}
              >
                {reducedTypes.map(({ id, name }) => {
                  return (
                    <Action
                      selected={id === type}
                      onClick={() => setType(id)}
                      label={name}
                      key={id}
                    />
                  );
                })}
              </Action>
              <Action
                custom={
                  <SimpleDatePicker
                    tooltip={'Set next release date'}
                    value={releaseDate}
                    onChange={handleChangeReleaseDate}
                  />
                }
              />
              <Action
                tooltip={'Add news'}
                icon={<AddCircleOutlineOutlinedIcon />}
                onClick={handleCreate}
                disabled={!isAllowed(NEWS_MANAGEMENT_PERMISSION)}
              />
            </Actions>
          </Stack>
        }
      />
      <SidePage.Content disableGutters={true} disableScroll={true}>
        <NewsList type={type} />
        {childComponent}
      </SidePage.Content>
    </SidePage>
  );
};
